import {
  ArrowRight,
  BarChart3,
  BookOpen,
  ChevronDown,
  Clock,
  ExternalLink,
  FolderOpen,
  Globe,
  Headphones,
  Languages,
  LayoutDashboard,
  LogOut,
  Mail,
  Mic,
  MoreVertical,
  Play,
  Search,
  Settings,
  Shield,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "@/services/apiService";
import authService from "@/services/authService";
import InboxBell from "@/components/shared/InboxBell";
import ThemeToggle from "@/components/shared/ThemeToggle";
import UserMenu from "@/components/shared/UserMenu";
import { toast } from "sonner";

const GOLD = "#c98905";

function getTimeGreeting(date = new Date()) {
  const hour = date.getHours();

  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 21) return "Good evening";
  return "Good night";
}

function Sparkline({ color = GOLD }) {
  return (
    <svg viewBox="0 0 92 34" className="h-9 w-20" aria-hidden="true">
      <path
        d="M2 29 C12 27 16 18 27 20 C38 23 42 8 52 12 C62 16 65 6 74 9 C82 12 84 6 90 4"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {[27, 52, 74, 90].map((x, index) => (
        <circle
          key={x}
          cx={x}
          cy={[20, 12, 9, 4][index]}
          r="3"
          fill={color}
        />
      ))}
    </svg>
  );
}

function StatCard({ icon: Icon, label, value, subLabel, tone = "gold" }) {
  const tones = {
    gold: {
      bg: "bg-[#fff2d8]",
      text: "text-[#b77900]",
      line: GOLD,
    },
    green: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      line: "#20833f",
    },
    violet: {
      bg: "bg-violet-50",
      text: "text-violet-700",
      line: "#6d3bd1",
    },
  };
  const selected = tones[tone] || tones.gold;

  return (
    <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_16px_45px_rgba(94,64,18,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_16px_45px_rgba(0,0,0,0.36)]">
      <CardContent className="flex h-full items-center justify-between gap-4 p-5">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ${selected.bg} ${selected.text}`}
          >
            <Icon className="h-8 w-8" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-[#d5c8ae]">
              {label}
            </p>
            <p
              className="mt-2 text-4xl font-bold leading-none text-slate-950 dark:text-[#f8efe0]"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {value}
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-[#9f947f]">{subLabel}</p>
          </div>
        </div>
        <Sparkline color={selected.line} />
      </CardContent>
    </Card>
  );
}

function SidebarItem({ icon: Icon, label, active, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-sm font-medium transition ${
        active
          ? "bg-gradient-to-r from-[#7a5200] to-[#c99220] text-white shadow-[0_0_24px_rgba(218,165,32,0.25)]"
          : "text-white/80 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon className="h-5 w-5 text-[#f2bd3b]" />
      <span className="flex-1">{label}</span>
      {badge ? (
        <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#e0a51b] px-2 text-xs font-bold text-black">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function QuickAction({ icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center gap-3 rounded-xl p-3 text-sm text-slate-700 transition hover:bg-[#fff8eb] dark:text-[#d5c8ae] dark:hover:bg-[#1a160d]"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f8edda] text-[#b77900] transition group-hover:bg-[#e4a61d] group-hover:text-black">
        <Icon className="h-7 w-7" />
      </span>
      <span>{label}</span>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [launchedBooks, setLaunchedBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [timeGreeting, setTimeGreeting] = useState(() => getTimeGreeting());

  useEffect(() => {
    loadDashboardData();
    loadUserInfo();
  }, []);

  useEffect(() => {
    const updateGreeting = () => setTimeGreeting(getTimeGreeting());
    updateGreeting();
    const intervalId = setInterval(updateGreeting, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const loadUserInfo = async () => {
    try {
      const userData = await authService.getMe();
      setUser(userData);
    } catch (error) {
      console.error("Error loading user info:", error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [metricsData, launchedData] = await Promise.all([
        apiService.getMetrics(),
        apiService.getLaunchedBooks(),
      ]);
      setMetrics(metricsData);
      setLaunchedBooks(launchedData);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        authService.clearTokens();
        navigate("/login");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        return;
      }
      try {
        const results = await apiService.searchBooks(query);
        setSearchResults(results);
        setShowSearchDropdown(results.length > 0);
      } catch (error) {
        console.error("Search error:", error);
      }
    }, 300),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    handleSearch(value);
  };

  const handleBookClick = async (bookId) => {
    try {
      const bookDetail = await apiService.getBookDetail(bookId);
      setSelectedBook(bookDetail);
      setShowBookModal(true);
      setShowSearchDropdown(false);
    } catch (error) {
      console.error("Error loading book:", error);
      toast.error("Failed to load book details");
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login");
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const displayName =
    user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "Alex";
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const books = launchedBooks.slice(0, 5);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fbf4e7] dark:bg-[#050504]">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#b77900]"></div>
          <p className="mt-4 text-slate-600 dark:text-[#d5c8ae]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f0df] text-slate-950 dark:bg-[#050504] dark:text-[#f8efe0]">
      <div className="flex min-h-screen">
        <aside className="hidden w-[300px] shrink-0 bg-[#070806] text-white shadow-2xl xl:block">
          <div className="sticky top-0 flex h-screen flex-col gap-8 overflow-y-auto px-6 py-7">
            <button
              className="flex w-full items-center text-left"
              onClick={() => navigate("/dashboard")}
            >
              <img
                src="/images/golden-records-real-logo.png"
                alt="Golden Records"
                className="h-36 w-full rounded-xl object-cover object-center"
              />
            </button>

            <nav className="space-y-2">
              <SidebarItem
                icon={LayoutDashboard}
                label="Dashboard"
                active
                onClick={() => navigate("/dashboard")}
              />
              <SidebarItem
                icon={BookOpen}
                label="Books"
                onClick={() => navigate("/books")}
              />
              <SidebarItem
                icon={FolderOpen}
                label="Projects"
                onClick={() => navigate("/publisher/workflow")}
              />
              <SidebarItem
                icon={Mic}
                label="Narrators"
                onClick={() => navigate("/narrator")}
              />
              <SidebarItem
                icon={FolderOpen}
                label="Work"
                onClick={() => navigate("/work")}
              />
              <SidebarItem
                icon={BarChart3}
                label="Publisher"
                onClick={() => navigate("/publisher/workflow")}
              />
              <SidebarItem
                icon={Mail}
                label="Inbox"
                onClick={() => navigate("/inbox")}
              />
              <SidebarItem icon={Settings} label="Settings" />
              {isAdmin ? (
                <SidebarItem
                  icon={Shield}
                  label="Admin"
                  onClick={() => navigate("/admin")}
                />
              ) : null}
            </nav>

            <div className="mt-auto overflow-hidden rounded-2xl border border-[#d6a42c]/35 bg-[radial-gradient(circle_at_75%_30%,rgba(224,165,27,0.35),transparent_28%),linear-gradient(145deg,#171104,#050505)] p-5 shadow-[0_0_40px_rgba(196,137,5,0.14)]">
              <p
                className="text-xl font-bold leading-snug text-[#f8d577]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Preserving stories.
                <br />
                Inspiring generations.
              </p>
              <p className="mt-4 text-sm leading-6 text-white/80">
                Join our global mission to safeguard humanity's greatest
                stories.
              </p>
              <Button
                onClick={() => navigate("/participate")}
                className="mt-5 bg-[#e1a51e] text-black hover:bg-[#f4c64e]"
              >
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-40 border-b border-[#e9dcc5] bg-white/90 px-4 py-4 backdrop-blur dark:border-[#2f260f] dark:bg-[#070705]/95 md:px-8">
            <div className="flex items-center gap-4">
              <button
                className="xl:hidden"
                onClick={() => navigate("/dashboard")}
              >
                <span
                  className="text-xl font-bold"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Golden Records
                </span>
              </button>

              <div className="relative ml-auto hidden w-full max-w-3xl md:block">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="global-search-input"
                  type="text"
                  placeholder="Search books, projects, narrators..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  className="h-12 rounded-2xl border-[#eadfcd] bg-white pl-12 pr-12 shadow-sm dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] dark:placeholder:text-[#8f8677]"
                />
                <Search className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#c98905]" />

                {showSearchDropdown && searchResults.length > 0 ? (
                  <div
                    className="absolute top-full z-50 mt-2 max-h-80 w-full overflow-y-auto rounded-2xl border border-[#eadfcd] bg-white shadow-xl"
                    data-testid="search-dropdown"
                  >
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleBookClick(result.id)}
                        className="w-full border-b border-[#f1e7d7] px-4 py-3 text-left last:border-0 hover:bg-[#fff8eb]"
                      >
                        <div className="font-medium text-slate-900">
                          {result.title}
                        </div>
                        <div className="text-xs text-slate-500">
                          {result.country} - {result.language}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <ThemeToggle />

              <InboxBell className="relative rounded-full p-3 text-slate-600 hover:bg-[#fff7e9] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]" />

              <UserMenu user={user} displayName={displayName} onLogout={handleLogout} />


              <Button
                variant="outline"
                onClick={handleLogout}
                className="hidden border-[#e8dcc8] bg-white hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710] md:inline-flex"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </header>

          <main className="space-y-6 px-4 py-7 md:px-8">
            <section className="relative overflow-hidden rounded-[28px] border border-[#eadfcd] bg-white shadow-[0_24px_80px_rgba(129,91,21,0.10)] dark:border-[#4d3d16] dark:bg-[#0d0c09] dark:shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
              <div className="absolute inset-y-0 right-0 w-full lg:w-[72%]">
                <img
                  src="/images/dashboard-gold-hero.png"
                  alt=""
                  className="h-full w-full object-contain object-right"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/25 dark:from-[#070705] dark:via-[#070705]/92 dark:to-[#070705]/25" />
              <div className="relative max-w-xl px-7 py-10 md:px-10">
                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#b77900]">
                  Golden Records
                </p>
                <h1
                  className="mt-5 text-4xl font-bold leading-tight text-slate-950 dark:text-[#f8efe0] md:text-5xl"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {timeGreeting}, {displayName}
                </h1>
                <p className="mt-4 max-w-md text-lg leading-8 text-slate-700 dark:text-[#d5c8ae]">
                  Let's continue preserving knowledge and inspiring the world.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button
                    onClick={() => navigate("/participate")}
                    className="bg-[#d99b13] text-black hover:bg-[#f0bd43]"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/publisher/workflow")}
                    className="border-[#d8bd7a] bg-white/70 text-[#8a5b00] hover:bg-[#fff6e3] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]"
                  >
                    New Project
                  </Button>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-4">
              <StatCard
                icon={BookOpen}
                label="Books Preserved"
                value={metrics?.books_accomplished || 0}
                subLabel="Global launches"
              />
              <StatCard
                icon={TrendingUp}
                label="Active Projects"
                value={metrics?.books_in_progress || 0}
                subLabel="In progress"
                tone="green"
              />
              <StatCard
                icon={Globe}
                label="Countries"
                value={metrics?.countries_participating || 0}
                subLabel="Participating"
                tone="violet"
              />
              <StatCard
                icon={Languages}
                label="Languages"
                value={metrics?.languages_covered || 0}
                subLabel="Covered"
              />
            </section>

            <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.12fr)_minmax(420px,0.88fr)]">
              <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_20px_60px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
                <CardHeader className="flex flex-col gap-4 border-b border-[#f0e4d2] dark:border-[#2f260f] md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#fff2d8] text-[#b77900]">
                      <Clock className="h-7 w-7" />
                    </div>
                    <div>
                      <CardTitle
                        className="text-xl dark:text-[#f8efe0]"
                        style={{ fontFamily: "Playfair Display, serif" }}
                      >
                        Recent Activity
                      </CardTitle>
                      <CardDescription className="dark:text-[#9f947f]">
                        Browse, listen and track launched audiobooks
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="border-[#eadfcd] bg-white text-slate-700 dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d]"
                  >
                    All Languages
                    <ChevronDown className="ml-2 h-4 w-4 text-[#c98905]" />
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[510px] overflow-y-auto">
                    {books.length === 0 ? (
                      <p className="px-6 py-10 text-center text-slate-500">
                        No launched books found
                      </p>
                    ) : (
                      books.map((book, index) => (
                        <div
                          key={book.id}
                          className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 border-b border-[#f2e7d7] px-6 py-4 last:border-0 hover:bg-[#fffaf0] dark:border-[#2f260f] dark:hover:bg-[#15120b]"
                          onClick={() => handleBookClick(book.id)}
                        >
                          <div className="grid h-14 w-14 place-items-center rounded-xl bg-gradient-to-br from-[#f6d77f] to-[#b77800] text-lg font-bold text-black shadow-md">
                            {book.title?.charAt(0) || "B"}
                          </div>
                          <div className="min-w-0">
                            <h3 className="truncate font-semibold text-slate-950 dark:text-[#f8efe0]">
                              {book.title}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-[#9f947f]">
                              {book.country} - {book.language}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="hidden items-center gap-1 text-sm text-slate-500 md:flex">
                              <Clock className="h-4 w-4" />
                              {[28, 12, 3, 8, 11][index] || 5}h{" "}
                              {[15, 45, 2, 40, 20][index] || 10}m
                            </span>
                            {book.listen_url ? (
                              <a
                                href={book.listen_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  size="sm"
                                  className="bg-[#c98905] text-white hover:bg-[#a56f00]"
                                >
                                  <Play className="mr-2 h-4 w-4 fill-white" />
                                  Listen
                                </Button>
                              </a>
                            ) : null}
                            <MoreVertical className="h-5 w-5 text-slate-500 dark:text-[#9f947f]" />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="border-t border-[#f0e4d2] p-5 text-center dark:border-[#2f260f]">
                    <button className="inline-flex items-center gap-2 font-semibold text-[#b77900] hover:text-[#8a5b00]">
                      View All Launched Books
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="relative min-h-[300px] overflow-hidden border-0 bg-[#090704] text-white shadow-[0_22px_60px_rgba(37,24,4,0.22)]">
                  <img
                    src="/images/dashboard-gold-hero.png"
                    alt=""
                    className="absolute inset-0 h-full w-full object-contain object-right opacity-70"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/15" />
                  <CardContent className="relative p-8">
                    <h2
                      className="max-w-xs text-3xl font-bold leading-tight"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      Be a Part of Something Timeless
                    </h2>
                    <p className="mt-4 max-w-sm text-white/86">
                      Share knowledge and preserve literature for future
                      generations.
                    </p>
                    <Button
                      onClick={() => navigate("/participate")}
                      className="mt-7 bg-[#e0a51b] text-black hover:bg-[#f4c64e]"
                    >
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_18px_55px_rgba(0,0,0,0.42)]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle
                      className="text-xl dark:text-[#f8efe0]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      Impact Overview
                    </CardTitle>
                    <Button
                      variant="outline"
                      className="h-9 border-[#eadfcd] bg-white text-xs dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d]"
                    >
                      This Month
                      <ChevronDown className="ml-2 h-4 w-4 text-[#c98905]" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        [Users, "1,254", "Listeners"],
                        [Headphones, "320", "Hours Listened"],
                        [UserPlus, "98", "New Contributors"],
                      ].map(([Icon, value, label]) => (
                        <div key={label} className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fff2d8] text-[#b77900]">
                            <Icon className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-950 dark:text-[#f8efe0]">{value}</p>
                            <p className="text-xs text-slate-500 dark:text-[#9f947f]">{label}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 h-12 overflow-hidden rounded-b-2xl bg-[linear-gradient(180deg,transparent,rgba(224,165,27,0.12))]">
                      <Sparkline color={GOLD} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-[#eadfcd] bg-white/95 shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_18px_55px_rgba(0,0,0,0.42)]">
                  <CardHeader className="pb-2">
                    <CardTitle
                      className="text-xl dark:text-[#f8efe0]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-2 md:grid-cols-4">
                    <QuickAction
                      icon={BookOpen}
                      label="Add Book"
                      onClick={() => navigate("/publisher/workflow")}
                    />
                    <QuickAction
                      icon={FolderOpen}
                      label="New Project"
                      onClick={() => navigate("/publisher/workflow")}
                    />
                    <QuickAction
                      icon={UserPlus}
                      label="Invite Narrator"
                      onClick={() => navigate("/narrator")}
                    />
                    <QuickAction icon={BarChart3} label="View Reports" />
                  </CardContent>
                </Card>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogContent className="max-w-2xl bg-white">
          {selectedBook ? (
            <>
              <DialogHeader>
                <DialogTitle
                  style={{ fontFamily: "Playfair Display, serif" }}
                  className="text-2xl text-slate-950"
                >
                  {selectedBook.title}
                </DialogTitle>
                <DialogDescription>
                  {selectedBook.description_short}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-[#fff8eb] p-3">
                    <p className="text-sm text-slate-600">Country</p>
                    <p className="font-semibold text-slate-900">
                      {selectedBook.country}
                    </p>
                  </div>

                  <div className="rounded-lg bg-[#fff8eb] p-3">
                    <p className="text-sm text-slate-600">Language</p>
                    <p className="font-semibold text-slate-900">
                      {selectedBook.language}
                    </p>
                  </div>

                  {selectedBook.narrator ? (
                    <div className="col-span-2 rounded-lg bg-[#fff8eb] p-3">
                      <p className="text-sm text-slate-600">Narrator</p>
                      <p className="font-semibold text-slate-900">
                        {selectedBook.narrator.display_name}
                      </p>
                    </div>
                  ) : null}
                </div>

                {selectedBook.listen_url ? (
                  <a
                    href={selectedBook.listen_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-[#c98905] text-white hover:bg-[#a56f00]">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Experience on Lysten AI
                    </Button>
                  </a>
                ) : null}

                {selectedBook.progress ? (
                  <div className="mt-6 space-y-4">
                    <h3
                      className="text-lg font-semibold"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      Progress Report
                    </h3>

                    {[
                      ["Editing", selectedBook.progress.editing_pct],
                      ["Verification", selectedBook.progress.verification_pct],
                      ["QC", selectedBook.progress.qc_pct],
                      ["Encryption", selectedBook.progress.encryption_pct],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div className="mb-1 flex justify-between">
                          <span className="text-sm font-medium text-slate-700">
                            {label}
                          </span>
                          <span className="text-sm font-semibold text-slate-900">
                            {value}%
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full bg-[#c98905] transition-all"
                            style={{ width: `${value}%` }}
                          />
                        </div>
                      </div>
                    ))}

                    <div className="border-t border-slate-200 pt-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-900">
                          Overall Progress
                        </span>
                        <span className="text-lg font-bold text-[#b77900]">
                          {selectedBook.progress.overall_progress.toFixed(1)}%
                        </span>
                      </div>

                      <div className="mt-2">
                        <span className="text-sm text-slate-600">
                          Launched:{" "}
                        </span>
                        <Badge
                          className={
                            selectedBook.launched
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }
                        >
                          {selectedBook.launched ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
